import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Wallet, PhoneIncoming, ChevronLeft, ChevronRight, Check, Infinity as InfinityIcon, Phone } from "lucide-react";

const SLIDES = [
  {
    key: "signup",
    step: "Step 1",
    icon: UserPlus,
    color: "#2563EB",
    title: "Sign up or log in",
    desc: "Create your free ZEEPTEL account in under a minute — just your name, email and the phone number we should ring you on.",
    cta: { label: "Create free account", to: "/register" },
  },
  {
    key: "credit",
    step: "Step 2",
    icon: Wallet,
    color: "#10B981",
    title: "Load credit — it never expires",
    desc: "Top up your wallet with a card in any of 22 currencies. Your credit stays yours forever: no expiry, no monthly fees, pay only for talk time.",
    cta: { label: "See top-up options", to: "/register" },
  },
  {
    key: "connect",
    step: "Step 3",
    icon: PhoneIncoming,
    color: "#E11D48",
    title: "Answer — we connect you",
    desc: "Dial any number and ZEEPTEL rings your phone first. Answer it, and you're instantly bridged to your receiver — no internet needed on either side. Fast and simple.",
    cta: { label: "Start calling", to: "/register" },
  },
];

function SignupVisual() {
  return (
    <div className="w-56 sm:w-64 mx-auto bg-white rounded-3xl border border-slate-200 shadow-2xl shadow-blue-900/10 p-5">
      <img src="/branding/zanitel_logo.png" alt="Zeeptel" className="h-6 w-auto mx-auto mb-4" />
      <div className="text-center font-heading font-bold text-sm mb-3">Create your account</div>
      {["Full name", "Email", "Password"].map((f) => (
        <div key={f} className="mb-2 h-9 rounded-xl bg-slate-100 border border-slate-200 flex items-center px-3 text-[11px] text-slate-400">
          {f}
        </div>
      ))}
      <div className="h-9 rounded-xl bg-[#2563EB] text-white text-xs font-bold flex items-center justify-center mt-3">
        Sign Up — it's free
      </div>
      <div className="text-center text-[10px] text-slate-400 mt-2">Already a member? Log in</div>
    </div>
  );
}

function CreditVisual() {
  return (
    <div className="w-56 sm:w-64 mx-auto bg-white rounded-3xl border border-slate-200 shadow-2xl shadow-emerald-900/10 p-5">
      <div className="rounded-2xl bg-[#1E3A8A] text-white p-4 mb-3">
        <div className="text-[10px] uppercase tracking-widest text-blue-200">Wallet balance</div>
        <div className="font-mono text-2xl font-bold mt-1">$25.00</div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {["$10", "$25", "$50"].map((a, i) => (
          <div key={a} className={`h-9 rounded-xl flex items-center justify-center text-xs font-bold border ${i === 1 ? "bg-emerald-500 text-white border-emerald-500" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
            {a}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1.5 justify-center text-[11px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full py-1.5 px-3">
        <InfinityIcon className="h-3.5 w-3.5" /> Credit never expires
      </div>
    </div>
  );
}

function ConnectVisual() {
  return (
    <div className="w-56 sm:w-64 mx-auto bg-slate-900 rounded-3xl border border-slate-700 shadow-2xl shadow-red-900/20 p-5 text-white">
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Incoming call</div>
        <div className="mt-3 h-14 w-14 mx-auto rounded-full bg-white flex items-center justify-center p-2">
          <img src="/branding/zanitel_mark.png" alt="Zeeptel" className="h-full w-auto" />
        </div>
        <div className="font-heading font-bold mt-2">ZEEPTEL</div>
        <div className="text-[11px] text-slate-400">connecting you to +234 801 234 5678</div>
      </div>
      <div className="flex items-center justify-center gap-6 mt-5">
        <div className="h-11 w-11 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
          <Phone className="h-4 w-4 text-red-400 rotate-[135deg]" />
        </div>
        <div className="h-12 w-12 rounded-full bg-emerald-500 flex items-center justify-center animate-pulse">
          <Phone className="h-5 w-5 text-white" />
        </div>
      </div>
      <div className="flex items-center gap-1.5 justify-center text-[11px] font-bold text-emerald-300 mt-4">
        <Check className="h-3.5 w-3.5" /> Answer &amp; talk — fast and simple
      </div>
    </div>
  );
}

const VISUALS = { signup: SignupVisual, credit: CreditVisual, connect: ConnectVisual };

export function HowToSlideshow() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return undefined;
    timer.current = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 5000);
    return () => clearInterval(timer.current);
  }, [paused]);

  const go = (i) => setIdx((i + SLIDES.length) % SLIDES.length);

  return (
    <div
      className="relative bg-white border border-slate-200 rounded-3xl shadow-lg overflow-hidden max-w-full"
      style={{ contain: "paint" }}
      data-testid="howto-slideshow"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${idx * 100}%)` }}>
        {SLIDES.map((s) => {
          const Visual = VISUALS[s.key];
          return (
            <div key={s.key} className="w-full shrink-0 grid md:grid-cols-2 items-center gap-6 p-8 sm:p-12" data-testid={`howto-slide-${s.key}`}>
              <div>
                <div className="inline-flex items-center gap-2 mb-4">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center shadow-lg" style={{ background: s.color }}>
                    <s.icon className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: s.color }}>{s.step} of 3</span>
                </div>
                <h3 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight text-[#0F172A]">{s.title}</h3>
                <p className="text-slate-500 text-sm sm:text-base mt-3 max-w-md">{s.desc}</p>
                <Link
                  to={s.cta.to}
                  data-testid={`howto-cta-${s.key}`}
                  className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md transition-transform hover:-translate-y-0.5"
                  style={{ background: s.color }}
                >
                  {s.cta.label} <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="py-2">
                <Visual />
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={() => go(idx - 1)}
        aria-label="Previous step"
        data-testid="howto-prev"
        className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-500 hover:text-[#2563EB] transition-colors"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={() => go(idx + 1)}
        aria-label="Next step"
        data-testid="howto-next"
        className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-500 hover:text-[#2563EB] transition-colors"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {SLIDES.map((s, i) => (
          <button
            key={s.key}
            onClick={() => go(i)}
            aria-label={`Go to ${s.title}`}
            data-testid={`howto-dot-${i}`}
            className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-[#E11D48]" : "w-2 bg-slate-300 hover:bg-slate-400"}`}
          />
        ))}
      </div>
    </div>
  );
}
